/**
 * @module services/patient-conditions
 * @description UC-REG-314 (Campana, Diego 2026-11-04) — "Mis patologías":
 *              el paciente marca sus patologías crónicas frecuentes
 *              (hipertensión, diabetes, embarazo, tuberculosis, salud
 *              mental, …) para seguimiento y prevención del Municipio.
 *              Los valores son ids del catálogo `ChronicCondition` de la
 *              organización (ABM del admin en Ajustes → Patologías
 *              crónicas) y se guardan en `User.patient.health.chronicConditions`
 *              vía `PUT /users/me`.
 *
 *              La API decide: la funcionalidad existe solo si
 *              `GET /organization/me` trae `capabilities.patientChronicConditions`
 *              (perfil del tenant por código, sin env vars). El Dashboard
 *              solo muestra u oculta.
 */
import api from "@/services/api";
import store from "@/store";
import chronicConditionService from "@/services/chronic-condition";
import { hasCapabilityIn } from "@/services/capabilities";

/** @type {string} Clave de la capacidad del tenant. */
export const CAPABILITY = "patientChronicConditions";

/**
 * @description ¿El tenant tiene encendido "Mis patologías"?
 * @param {Object|null} organizationContext - `store.state.organizationContext`
 * @returns {boolean}
 */
export function hasPatientConditionsCapability(organizationContext) {
  return hasCapabilityIn(organizationContext, CAPABILITY);
}

/**
 * @description Ids de patologías marcadas por el usuario (acepta ids o docs populados).
 * @param {Object|null} user - usuario (con `patient.health.chronicConditions`)
 * @returns {string[]}
 */
export function selectedConditionIds(user) {
  const list =
    (user &&
      user.patient &&
      user.patient.health &&
      user.patient.health.chronicConditions) ||
    [];
  return list
    .filter((c) => c !== null && c !== undefined)
    .map((c) => (c && c._id ? String(c._id) : String(c)));
}

/**
 * @description Agrega o quita un id de la selección, sin mutar la original.
 * @param {string[]} ids - selección actual
 * @param {string} id - id a alternar
 * @returns {string[]} selección nueva
 */
export function toggleConditionId(ids, id) {
  return ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id];
}

/**
 * @description Nombres de las patologías marcadas, en el orden del catálogo.
 * @param {string[]} ids - ids marcados
 * @param {Array<{_id:string,name:string}>} catalog - catálogo activo
 * @returns {string[]}
 */
export function conditionNames(ids, catalog) {
  const set = new Set((ids || []).map(String));
  return (catalog || [])
    .filter((c) => set.has(String(c._id)))
    .map((c) => c.name);
}

/**
 * @description Catálogo activo de patologías crónicas de la organización.
 *              Sin filtro `isActive`: el endpoint devuelve solo activas por
 *              default (y con `isActive=true` el middleware de la API lo
 *              castea a booleano y el handler lo compara como string → vacío).
 * @returns {Promise<Array<{_id:string,code:string,name:string}>>}
 */
export function loadConditionsCatalog() {
  return chronicConditionService
    .query({ limit: 200 })
    .then((res) => (Array.isArray(res) ? res : (res && res.docs) || []));
}

/**
 * @description Guarda las patologías marcadas (`PUT /users/me`) y mergea
 *              `patient.health` de la respuesta en el usuario del store sin
 *              pisar el resto del subdocumento `patient`.
 * @param {string[]} ids - ids del catálogo
 * @returns {Promise<string[]>} ids guardados según la API
 */
export function saveMyConditions(ids) {
  return api
    .put("/users/me", { patient: { health: { chronicConditions: ids } } })
    .then((res) => {
      const saved = (res && res.data) || {};
      const existing = store.state.user || {};
      const existingPatient = existing.patient || {};
      const savedHealth = (saved.patient && saved.patient.health) || {};
      store.commit("setUser", {
        ...existing,
        patient: {
          ...existingPatient,
          health: { ...(existingPatient.health || {}), ...savedHealth },
        },
      });
      return selectedConditionIds(saved);
    });
}

export default {
  CAPABILITY,
  hasPatientConditionsCapability,
  selectedConditionIds,
  toggleConditionId,
  conditionNames,
  loadConditionsCatalog,
  saveMyConditions,
};
